import React, { useState } from "react";
import { Table, Spin, Pagination, Tag } from "antd";
import { useOrders } from "../../hooks/useOrders";
import { useAuth } from "../../hooks/useAuth";

interface OrdersTableProps {
  orderType: number;
  filters: Record<string, any>;
  canViewAllUsersOrders: boolean;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  orderType,
  filters,
  canViewAllUsersOrders,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: user } = useAuth();

  // If user doesn't have permission to view all orders,
  // only show orders where user is assigned as waiter or where waiter is null
  const finalFilters = { ...filters };
  if (!canViewAllUsersOrders && user) {
    // This will be interpreted by the API to filter to orders
    // where the user is the waiter or the waiter is null
    finalFilters.waiter = user.name;
  }

  // Add order status filter
  finalFilters.order_status_id = orderType;

  const { data: ordersData, isLoading } = useOrders(currentPage, finalFilters);

  const columns = [
    { title: "Order ID", dataIndex: "reference", key: "reference" },
    {
      title: "Customer",
      dataIndex: "user",
      key: "user",
      render: (user: { name: string; user_type?: { icon?: string } }) => (
        <div className="flex items-center space-x-2">
          <img
            src={user?.user_type?.icon || "/default-avatar.png"}
            alt="icon"
            className="w-5 h-5 rounded-full"
          />
          <span>{user?.name || "N/A"}</span>
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location: { name: string }) => location?.name || "N/A",
    },
    {
      title: "Table Number",
      dataIndex: "table",
      key: "table",
      render: (table: { number: string }) => table?.number || "N/A",
    },
    {
      title: "Status",
      dataIndex: "order_status",
      key: "order_status",
      render: (order_status: { name: string }) => (
        <Tag color="blue" className="capitalize">
          {order_status?.name || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Waiter",
      dataIndex: "waiter",
      key: "waiter",
      render: (waiter: string | null) => (
        <span>{waiter ? waiter : "Unassigned"}</span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (payment_status: { name: string }) => {
        let color = "default"; // Default color for unknown statuses
        if (payment_status?.name === "Pending") {
          color = "default";
        } else {
          color = "green";
        }

        return (
          <Tag color={color} className="capitalize">
            {payment_status?.name || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Total Items",
      dataIndex: "order_items",
      key: "order_items",
      render: (order_items: any[]) => order_items?.length ?? 0,
    },
    {
      title: "Total Amount",
      dataIndex: "order_items",
      key: "total_amount",
      render: (order_items: any[]) => {
        const totalAmount = order_items
          ?.reduce(
            (total, item) => total + parseFloat(item.amount) * item.qty,
            0
          )
          .toFixed(2);
        return `₦${parseFloat(totalAmount).toLocaleString()}`;
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) =>
        new Date(date).toLocaleDateString("en-US") || "N/A",
    },
    {
      title: "Action",
      key: "action",
      render: (_: unknown, record: { id: number }) => (
        <a
          href={`/orders/${record.id}`}
          className="text-blue-500 hover:underline"
        >
          View
        </a>
      ),
    },
  ];

  return (
    <div>
      {/* Show loading spinner */}
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Render Table */}
          <Table
            className="custom-table"
            columns={columns}
            dataSource={ordersData?.data ?? []}
            pagination={false}
          />
          {/* Pagination */}
          <div className="flex justify-end mt-4">
            <Pagination
              current={ordersData?.meta.current_page ?? 1}
              total={ordersData?.meta.total ?? 0}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default OrdersTable;
